import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from 'v-select2-multiple-component';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";

export default {
    name: "scoreSettingImport",
    components: {
        MainLayout,
        Select2Multiple,
        Loading,
    },
    mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

    data() {
        return {
            isLoading: false,
            acdYearList: [],
            templateAcademicYearId: null,
            academicYearId: null,
            submitted: false,
            templateSubmitted: false,
            invalidFileType: false,
            invalidFileSize: false,
            file_name: '',
            importErrorMessage: '',
            disableInputFields: false,
            disableValidateBtn: false,
            disableConfirmBtn: false,
            importStep: 'upload',
            previewRows: [],
            previewTotal: 0,
        };
    },

    validations: {
        academicYearId: { required },
        file_name: { required },
    },

    computed: {
        selectedAcademicYearName() {
            const selected = this.acdYearList.find(
                (period) => String(period.id) === String(this.academicYearId)
            );
            return selected?.text || '';
        },

        disableTemplateBtn() {
            return this.importStep === 'preview';
        },
    },

    watch: {
        templateAcademicYearId() {
            this.importErrorMessage = '';
        },
        academicYearId() {
            if (this.validationErrors.academic_year_id) {
                this.$delete(this.validationErrors, 'academic_year_id');
            }
            this.importErrorMessage = '';
        },
        importStep(value) {
            if (value === 'preview') {
                this.disableInputFields = true;
            } else {
                this.disableInputFields = false;
            }
        }
    },

    methods: {
        async getParentAcdYearList() {
            try {
                this.isLoading = true;
                const response = await axios.get('period/periodList');
                if (response.data) {
                    this.acdYearList = response.data.data || [];
                }
            } catch (error) {
                this.handleError(error);
            } finally {
                this.isLoading = false;
            }
        },

        chooseFile() {
            this.$refs.fileUploader.value = null;
            this.file_name = '';
            this.invalidFileType = false;
            this.invalidFileSize = false;
            this.importErrorMessage = '';
            this.clearImportConfirmErrors();
            this.$refs.fileUploader.click();
            if (this.validationErrors.file) {
                this.$delete(this.validationErrors, 'file');
            }
        },

        onFileChange(event) {
            const file = event.target.files[0];
            if (!file) {
                this.file_name = '';
                return;
            }

            const allowedTypes = ['.xls', '.xlsx'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                this.invalidFileType = true;
                this.file_name = '';
                return;
            }

            this.invalidFileType = false;
            this.invalidFileSize = false;
            this.importErrorMessage = '';
            this.file_name = file.name;
        },

        getTemplateFileName() {
            const selected = this.acdYearList.find(
                (period) => String(period.id) === String(this.templateAcademicYearId)
            );
            const periodName = (selected?.text || "all").replace(/\s+/g, "_");
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, "0");
            const day = String(now.getDate()).padStart(2, "0");
            return `${periodName}_score_setting_${year}_${month}_${day}.xlsx`;
        },

        async templateDownload() {
            this.templateSubmitted = true;
            this.importErrorMessage = '';

            if (this.disableTemplateBtn) {
                return;
            }

            const params = {};
            if (this.templateAcademicYearId) {
                params.academic_year_id = this.templateAcademicYearId;
            }

            this.isLoading = true;
            this.disableInputFields = true;
            try {
                const response = await axios({
                    method: 'get',
                    url: '/score-setting/template-download',
                    params,
                    responseType: 'blob',
                });

                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = this.getTemplateFileName();
                document.body.appendChild(link);
                link.click();
                window.URL.revokeObjectURL(link.href);
            } catch (error) {
                if (error.response?.status === 422) {
                    this.importErrorMessage = error.response.data.message || 'Unable to download template.';
                    this.$notification.error(this.importErrorMessage, {
                        timer: 4,
                        position: "bottomCenter",
                    });
                } else {
                    this.handleError(error);
                }
            } finally {
                this.isLoading = false;
                this.disableInputFields = false;
            }
        },

        /**
         * Step 1: Validate import and show preview.
         */
        async validateImport() {
            this.submitted = true;
            this.importErrorMessage = '';
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const file = this.$refs.fileUploader.files[0];
            if (!file) {
                this.invalidFileType = true;
                this.file_name = '';
                return;
            }

            const maxFileSizeBytes = 5 * 1024 * 1024;
            if (file.size > maxFileSizeBytes) {
                this.invalidFileSize = true;
                return;
            }

            this.invalidFileSize = false;
            await this.doValidateImport();
        },

        async doValidateImport() {
            this.isLoading = true;
            this.disableValidateBtn = true;

            const formData = new FormData();
            formData.append('academic_year_id', this.academicYearId);
            formData.append('file', this.$refs.fileUploader.files[0]);

            try {
                const response = await axios.post('/score-setting/import/validate', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                });

                this.previewRows = this.clonePreviewRows(response.data.preview || []);
                this.previewTotal = response.data.total || this.previewRows.length;
                this.clearImportConfirmErrors();
                this.importStep = 'preview';
            } catch (error) {
                if (!error.response) {
                    this.$notification.error("Something went wrong. Please re-select the file and try again.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                    this.file_name = '';
                    this.$refs.fileUploader.value = null;
                    this.submitted = false;
                } else if (error.response.status === 422) {
                    this.handleImportValidationError(error);
                } else {
                    this.handleError(error);
                }
            } finally {
                this.disableValidateBtn = false;
                this.isLoading = false;
            }
        },

        /**
         * Step 2: Confirm and persist the validated preview.
         */
        async confirmImportSubmit() {
            this.clearImportConfirmErrors();

            if (!this.previewRows.length) {
                this.$notification.error("No preview data to import.", {
                    timer: 3,
                    position: "bottomCenter",
                });
                return;
            }

            const fieldErrors = this.validatePreviewBeforeConfirm();
            if (Object.keys(fieldErrors).length) {
                this.setConfirmFieldErrors(fieldErrors);
                return;
            }

            this.isLoading = true;
            this.disableConfirmBtn = true;

            try {
                const response = await axios.post('/score-setting/import/confirm', {
                    academic_year_id: this.academicYearId,
                    preview: this.buildConfirmPreviewPayload(),
                });

                this.$notification.success(response.data.message || 'Score Settings imported successfully.', {
                    timer: 3,
                    position: "bottomCenter",
                });
                this.showList();
            } catch (error) {
                if (!error.response) {
                    this.$notification.error("Network error. Please try again.", {
                        timer: 3,
                        position: "bottomCenter",
                    });
                } else if (error.response.status === 422) {
                    this.handleConfirmBackendError(error);
                } else {
                    this.handleError(error);
                }
            } finally {
                this.disableConfirmBtn = false;
                this.isLoading = false;
            }
        },

        clonePreviewRows(rows) {
            return (rows || []).map((row) => ({
                grade_name: String(row.grade_name || "").trim(),
                campus_grade_id: row.campus_grade_id,
                term_exam_rate: row.term_exam_rate ?? "",
                unit_test_rate: row.unit_test_rate ?? "",
                project_rate: row.project_rate ?? "",
            }));
        },

        buildConfirmPreviewPayload() {
            return this.previewRows.map((row) => ({
                grade_name: String(row.grade_name || "").trim(),
                term_exam_rate: this.normalizeRateValue(row.term_exam_rate),
                unit_test_rate: this.normalizeRateValue(row.unit_test_rate),
                project_rate: this.normalizeRateValue(row.project_rate),
            }));
        },

        normalizeRateValue(value) {
            if (value === null || value === undefined || String(value).trim() === "") {
                return null;
            }

            const trimmed = String(value).trim();
            if (!/^-?\d+$/.test(trimmed)) {
                return trimmed;
            }

            return Number(trimmed);
        },

        validatePreviewBeforeConfirm() {
            const errors = {};
            const seenGradeNames = {};

            this.previewRows.forEach((row, index) => {
                const rowErrors = {};
                const gradeName = String(row.grade_name || "").trim();
                const gradeLookupKey = gradeName.toLowerCase();

                if (!gradeName) {
                    rowErrors.grade_name = "Grade Name is required.";
                } else if (seenGradeNames[gradeLookupKey]) {
                    rowErrors.grade_name = "Duplicate grade in preview.";
                } else {
                    seenGradeNames[gradeLookupKey] = true;
                }

                const rateFields = [
                    { key: "term_exam_rate" },
                    { key: "unit_test_rate" },
                    { key: "project_rate" },
                ];

                const parsedRates = {};
                let hasRateError = false;

                rateFields.forEach(({ key }) => {
                    const normalized = this.normalizeRateValue(row[key]);

                    if (normalized === null || !/^-?\d+$/.test(String(normalized))) {
                        rowErrors[key] = "Must be integer 0–100.";
                        hasRateError = true;
                        return;
                    }

                    const numericRate = Number(normalized);

                    if (numericRate < 0 || numericRate > 100) {
                        rowErrors[key] = "Must be integer 0–100.";
                        hasRateError = true;
                        return;
                    }

                    parsedRates[key] = numericRate;
                });

                if (!hasRateError && gradeName) {
                    const sum = parsedRates.term_exam_rate + parsedRates.unit_test_rate + parsedRates.project_rate;
                    if (sum !== 100) {
                        const sumMsg = `Total sum must be 100. (current sum is ${sum}).`;
                        rowErrors.term_exam_rate = sumMsg;
                        rowErrors.unit_test_rate = sumMsg;
                        rowErrors.project_rate = sumMsg;
                    }
                }

                if (Object.keys(rowErrors).length) {
                    errors[index] = rowErrors;
                }
            });

            return errors;
        },

        collectValidationMessages(data, fallback) {
            if (Array.isArray(data.errors) && data.errors.length) {
                return data.errors;
            }

            const customMessages = data.errors?.custom_validation_messages;
            if (Array.isArray(customMessages) && customMessages.length) {
                return customMessages;
            }

            if (data.errors && typeof data.errors === "object") {
                const flat = Object.values(data.errors).flat();
                if (flat.length) {
                    return flat;
                }
            }

            if (data.msg) {
                return String(data.msg).split("\n").filter(Boolean);
            }

            if (data.message) {
                return String(data.message).split("\n").filter(Boolean);
            }

            return [fallback];
        },

        handleImportValidationError(error) {
            if (error.response?.status === 422) {
                const data = error.response.data || {};
                const messages = this.collectValidationMessages(data, "Import validation failed.");

                window.alert(messages.join("\n"));
                return;
            }

            this.handleError(error);
        },

        handleConfirmBackendError(error) {
            if (this.applyConfirmBackendErrors(error, this.getImportConfirmErrorOptions())) {
                return;
            }
            this.handleError(error);
        },

        getImportConfirmErrorOptions() {
            return {};
        },

        backToUpload() {
            this.importStep = 'upload';
            this.previewRows = [];
            this.previewTotal = 0;
        },

        cancelPreview() {
            this.resetAll();
        },

        resetAll() {
            this.submitted = false;
            this.templateSubmitted = false;
            this.disableInputFields = false;
            this.templateAcademicYearId = null;
            this.academicYearId = null;
            this.file_name = '';
            this.invalidFileType = false;
            this.invalidFileSize = false;
            this.importErrorMessage = '';
            this.clearImportConfirmErrors();
            this.importStep = 'upload';
            this.previewRows = [];
            this.previewTotal = 0;
            this.$v.$reset();
            if (this.$refs.fileUploader) {
                this.$refs.fileUploader.value = null;
            }
            this.getParentAcdYearList();
        },

        showList() {
            this.$router.push({
                path: "/score-setting"
            });
        },

        handleError(error) {
            if (error.response?.status === 401) {
                this.$store.commit('logout');
                window.location.replace('login');
            } else {
                console.error(error);
            }
        },
    },

    mounted() {
        this.getParentAcdYearList();
    },
};
